// ============================================================================
// CONTENT FILTER (client side)
// ----------------------------------------------------------------------------
// Catches racial and ethnic slurs in chat messages and usernames BEFORE they
// are sent, so the player gets an immediate warning and nothing leaves the
// device. Deliberately narrow: no profanity filter, no other categories.
// Trash talk is part of poker; racism is the one line.
//
// The server runs the same detector on every stored message (functions/src
// index.ts, detectHateSpeech) and hands out strikes, mutes and auto-reports
// to anything that gets past this gate. Keep the two term lists identical.
//
// Matching: lowercase, undo leetspeak, collapse repeated letters, then
//   1. whole-word match on each token (so "raccoon" / "spicy" never trip)
//   2. long terms and phrases matched with all spaces removed (so
//      "n i g g e r" and "white  power" do)
// ============================================================================

const HATE_TERMS_WORD: string[] = [
  // racial / ethnic
  'nigger', 'nigga', 'niqqa', 'niqqer', 'nig', 'coon', 'jigaboo', 'porchmonkey', 'darkie', 'darky',
  'pickaninny', 'golliwog',
  'chink', 'gook', 'zipperhead', 'chinaman',
  'spic', 'spick', 'wetback', 'beaner',
  'kike', 'heeb', 'hymie', 'yid',
  'towelhead', 'raghead', 'sandnigger', 'cameljockey',
  'wop', 'dago', 'paki', 'injun', 'squaw', 'honky', 'wigger', 'tarbaby', 'junglebunny',
];
const HATE_PHRASES_COLLAPSED: string[] = [
  'whitepower', 'whitesupremacy', 'heilhitler', 'siegheil', 'ethniccleansing', 'gasthejews',
];
const HATE_TERMS_COLLAPSED: string[] = [
  ...HATE_TERMS_WORD.filter(t => t.length >= 6),
  'nigga', // five letters, but spaced-out use is common enough to cover
  ...HATE_PHRASES_COLLAPSED,
];
const HATE_WORD_REGEX = new RegExp(`^(${HATE_TERMS_WORD.join('|')}|1488)(s|z|es|er|ers|a|as|ing|ed|ish)?$`);

function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/0/g, 'o').replace(/[1!|]/g, 'i').replace(/3/g, 'e')
    .replace(/[4@]/g, 'a').replace(/[5$]/g, 's').replace(/7/g, 't')
    .replace(/(.)\1{2,}/g, '$1$1');
}

/** Returns the matched term when the text contains hate speech. */
export function checkContent(text: string): { hit: boolean; term?: string } {
  if (!text) return { hit: false };
  // "1488" is checked before leet normalization turns it into letters.
  if (/\b1488\b/.test(text)) return { hit: true, term: '1488' };
  const norm = normalize(text);
  for (const token of norm.split(/[^a-z]+/)) {
    if (!token) continue;
    const m = token.match(HATE_WORD_REGEX);
    if (m) return { hit: true, term: m[1] };
  }
  const collapsed = norm.replace(/[^a-z]/g, '');
  for (const term of HATE_TERMS_COLLAPSED) {
    if (collapsed.includes(term)) return { hit: true, term };
  }
  return { hit: false };
}

/** True when the text is clean. */
export function isContentClean(text: string): boolean {
  return !checkContent(text).hit;
}

export function getFilterMessage(context: 'username' | 'chat'): string {
  if (context === 'username') {
    return 'This username contains language that is not allowed. Please choose a different name.';
  }
  return 'That message was not sent. Racial slurs are not allowed on PokerSweepStars.';
}

const contentFilter = { checkContent, isContentClean, getFilterMessage };
export default contentFilter;
